import { useEffect, useState, useCallback } from "react";
import axios from "../customAxios.js";
import { useAuth } from "./useAuth.jsx";

/**
 * Custom Hook to manage current user status and related actions
 *
 * It is responsible for obtaining user data when mounting, and provides
 * functions to update the name, email, password and handle security codes
 * Automatically controls loading and error states
 *
 * @returns {{
 * currentUser: object | null,
 * loading: boolean,
 * error: string | null,
 * setError: React.Dispatch<React.SetStateAction<string | null>>,
 * setLoading: React.Dispatch<React.SetStateAction<boolean>>,
 * updateName: (data: { first_name: string, last_name: string }) => Promise<void>,
 * updateEmail: (data: { email: string }) => Promise<void>,
 * requestSecureCode: () => Promise<void>,
 * changePassword: (data: { password: string, token: string }) => Promise<void>,
 * verifySecureCode: (code: string) => Promise<boolean, string>
 * }} An object with user status and functions to interact with the API
 *
 * @example
 * const { user, loading, error, updateName } = useUser();
 */

export function useUser() {
  // Data status and IU
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { logoutLocal } = useAuth();

  // STATE MANNERS (HANDLERS)
  const getCurrentUser = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: user } = await axios.get("get_user/");
      setCurrentUser(user);
    } catch (error) {
      console.error(`Error al obtener datos del usuario actual: ${error}`);
      setError("Ocurrió un error al obtener datos del usuario");
    } finally {
      setLoading(false);
    }
  }, []);

  const updateName = useCallback(
    async (data) => {
      setLoading(true);
      setError(null);
      try {
        const { data: user } = await axios.patch(
          `update_user_names/${currentUser.id}/`,
          data
        );
        setCurrentUser(user);
      } catch (error) {
        console.error(`Error al actualizar los datos del usuario: ${error}`);
        setError("Ocurrió un error al actualizar los datos del usuario");
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [currentUser]
  );

  const updateEmail = useCallback(
    async (data) => {
      setLoading(true);
      setError(null);
      try {
        const { data: user } = await axios.patch(
          `update_email/${currentUser.id}/`,
          data
        );
        setCurrentUser(user);
      } catch (error) {
        console.error(`Error al actualizar los datos del usuario: ${error}`);
        setError("Ocurrió un error al actualizar los datos del usuario");
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [currentUser]
  );

  const requestSecureCode = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      await axios.get("get_secure_code/");
    } catch (error) {
      console.error(`Error al generar el codigo de seguridad: ${error}`);
      setError("Error al generar el codigo de seguridad");
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const changePassword = useCallback(
    async (password, token) => {
      setLoading(true);
      setError(null);
      try {
        await axios.post("change_password/", {
          token,
          new_password1: password,
          new_password2: password,
        });
        alert(
          "Contraseña cambiada con éxito. Por favor, inicia sesión nuevamente"
        );
        logoutLocal();
      } catch (error) {
        console.error(`Error al generar el codigo de seguridad: ${error}`);
        setError("Error al generar el codigo de seguridad");
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [logoutLocal]
  );

  const verifySecureCode = useCallback(async (code) => {
    setLoading(true);
    setError(null);
    try {
      const {
        data: { token, valid },
      } = await axios.post("verify_secure_code/", {
        code,
      });
      return { valid, token };
    } catch (error) {
      console.error(`Error al generar el codigo de seguridad: ${error}`);
      setError("Error al generar el codigo de seguridad");
      return { valid: false, error };
    } finally {
      setLoading(false);
    }
  }, []);

  const updateOperativeMails = useCallback(
    async (emails) => {
      setLoading(true);
      setError(null);
      try {
        const { data: user } = await axios.patch(
          `update_operative_mails/${currentUser.id}/`,
          { operative_mails: emails }
        );
        setCurrentUser(user);
      } catch (error) {
        console.error(
          `No se pudieron actualizar los correos operativos: ${error}`
        );
        setError("No se pudieron actualizar los correos operativos");
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [currentUser]
  );

  // SECONDARY EFFECTS (FETCH, DEBUNCE)
  useEffect(() => {
    getCurrentUser();
  }, [getCurrentUser]);

  // PUBLIC API OF THE HOOK
  return {
    // State and Data
    user: currentUser,
    loading,
    error,

    // MANNERS (Setters y Actions)
    setError,
    setLoading,
    updateName,
    updateEmail,
    requestSecureCode,
    changePassword,
    verifySecureCode,
    updateOperativeMails,
  };
}
